import { useEffect, useRef, useState } from 'react';
import { useAccessToken } from '@/auth/useAccessToken';
import { attachmentsApi } from './attachmentsApi';

/**
 * Fetches an attachment with the bearer token and returns a blob object URL
 * (revoked on unmount / id change). The download endpoint requires auth, so a
 * plain <img src> can't be used — every attachment image goes through this.
 *
 * The token getter is held in a ref and intentionally NOT a dependency: the
 * effect should re-run only when the id changes, never per render (that caused
 * an infinite fetch loop).
 */
export function useAttachmentObjectUrl(id: string): string | null {
  const getAccessToken = useAccessToken();
  const tokenRef = useRef(getAccessToken);
  tokenRef.current = getAccessToken;
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    setUrl(null);
    attachmentsApi
      .fetchBlob(id, () => tokenRef.current())
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id]);

  return url;
}
